
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { baseUrl } from "../Univ.js";

import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
} from "@mui/material";

import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Link } from "react-router-dom";

const PurchaseGstReport = (props) => {
    const [startDate, setStartDate] = useState(
        new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    );
    const [endDate, setEndDate] = useState(new Date());

    const [addedby, setAddedby] = useState("");
    const [mode, setMode] = useState("");

    const [userData, setUserData] = useState([]);
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const formatDate = (date) => {
        if (!date) return "";

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
    };

    const splitValues = (value) => {
        if (value === null || value === undefined || value === "") {
            return [];
        }

        return String(value).split(",").map((item) => item.trim());
    };

    const getNumber = (value) => {
        const number = parseFloat(
            String(value ?? "0").replace(/,/g, "").trim()
        );

        return Number.isFinite(number) ? number : 0;
    };

    const formatAmount = (value) => {
        return getNumber(value).toFixed(2);
    };

    // ---------------------------------------------------------
    // LOAD USERS
    // ---------------------------------------------------------

    const loadUsers = useCallback(async () => {
        try {
            const response = await axios.get(`${baseUrl}user-search/`);

            const data = Array.isArray(response.data)
                ? response.data
                : response.data?.results || [];

            setUserData(data);
        } catch (error) {
            console.error("Unable to load users:", error);
            setUserData([]);
        }
    }, []);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    // ---------------------------------------------------------
    // DRUG-WISE GRN TRANSFORMATION
    // ---------------------------------------------------------

    const expandGrnDrugWise = (grnList) => {
        const expandedRows = [];

        (Array.isArray(grnList) ? grnList : []).forEach((grn) => {
            const products = splitValues(grn.product);
            const quantities = splitValues(grn.totqty);
            const expiries = splitValues(grn.expiry);
            const cgstRates = splitValues(grn.cgst);
            const batches = splitValues(grn.batch);
            const gstAmounts = splitValues(grn.gstamount);
            const netAmounts = splitValues(grn.netamount);

            const drugCount = Math.max(
                products.length,
                quantities.length,
                expiries.length,
                cgstRates.length,
                batches.length,
                gstAmounts.length,
                netAmounts.length,
                1
            );

            for (let index = 0; index < drugCount; index++) {
                const gstAmount = getNumber(gstAmounts[index]);
                const cgstAmount = gstAmount / 2;
                const sgstAmount = gstAmount / 2;

                expandedRows.push({
                    id: `${grn.id}-${index}`,

                    grn_id: grn.id,
                    grn_no: grn.grn_no || "",

                    grn_date_date: grn.grn_date_date || "",
                    add_time: grn.add_time || "",

                    type: grn.type || "",
                    addedby: grn.addedby || "",

                    dist__name: grn.dist__name || "",
                    dist__gst: grn.dist__gst || "",

                    invoice: grn.invoice || "",
                    inv_date: grn.inv_date || "",

                    product: products[index] || "",
                    totqty: quantities[index] || "",
                    expiry: expiries[index] || "",
                    batch: batches[index] || "",

                    cgst_percent: getNumber(cgstRates[index]),
                    sgst_percent: getNumber(cgstRates[index]),

                    gstamount: gstAmount,
                    cgst_amount: cgstAmount,
                    sgst_amount: sgstAmount,

                    netamount: getNumber(netAmounts[index]),

                    // GRN-level amounts are stored only on the first
                    // drug row to prevent duplicate report totals.
                    grnamount:
                        index === 0 ? getNumber(grn.grnamount) : 0,

                    invamount:
                        index === 0 ? getNumber(grn.invamount) : 0,

                    drug_index: index,
                    drug_count: drugCount,
                });
            }
        });

        return expandedRows;
    };

    // ---------------------------------------------------------
    // FETCH PURCHASE GST REPORT
    // ---------------------------------------------------------

    const getPurchaseReport = useCallback(async () => {
        if (!startDate || !endDate) {
            return;
        }

        setIsLoading(true);

        try {
            const startDateString = formatDate(startDate);
            const endDateString = formatDate(endDate);

            const response = await axios.get(
                `${baseUrl}all-pharm-grn/`,
                {
                    params: {
                        start_date: startDateString,
                        end_date: endDateString,
                        addedby: addedby,
                        mode: mode,
                        status: 1,
                    },
                }
            );

            const data = Array.isArray(response.data)
                ? response.data
                : response.data?.results || [];

            setRows(expandGrnDrugWise(data));
        } catch (error) {
            console.error("Purchase GST report error:", error);
            setRows([]);
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate, addedby, mode]);

    useEffect(() => {
        getPurchaseReport();
    }, [getPurchaseReport]);

    // ---------------------------------------------------------
    // TOTALS
    // ---------------------------------------------------------

    const totals = useMemo(() => {
        return rows.reduce(
            (result, row) => {
                result.quantity += getNumber(row.totqty);

                result.gst += getNumber(row.gstamount);
                result.cgst += getNumber(row.cgst_amount);
                result.sgst += getNumber(row.sgst_amount);

                result.netamount += getNumber(row.netamount);

                // These values are populated only on the first
                // drug row of each GRN.
                result.grnamount += getNumber(row.grnamount);
                result.invamount += getNumber(row.invamount);

                return result;
            },
            {
                quantity: 0,
                gst: 0,
                cgst: 0,
                sgst: 0,
                netamount: 0,
                grnamount: 0,
                invamount: 0,
            }
        );
    }, [rows]);

    const uniqueGrnCount = useMemo(() => {
        return new Set(rows.map((row) => row.grn_id)).size;
    }, [rows]);

    const totalPurchaseBeforeGst = totals.invamount - totals.gst;

    // ---------------------------------------------------------
    // DATAGRID COLUMNS
    // ---------------------------------------------------------

    const columns = useMemo(
        () => [
            {
                field: "grn_date_date",
                headerName: "GRN Date",
                width: 110,
            },
            {
                field: "add_time",
                headerName: "Time",
                width: 95,
            },
            {
                field: "grn_no",
                headerName: "GRN No",
                width: 145,
                renderCell: (params) => (
                    <Link
                        to={`/grn-invoice/${params.row.grn_no}`}
                        target="_blank"
                        style={{
                            color: "#1976d2",
                            fontWeight: 600,
                            textDecoration: "none",
                        }}
                    >
                        {params.value}
                    </Link>
                ),
            },
            {
                field: "type",
                headerName: "Payment Mode",
                width: 135,
            },
            {
                field: "dist__name",
                headerName: "Distributor",
                width: 190,
            },
            {
                field: "dist__gst",
                headerName: "Distributor GSTIN",
                width: 165,
            },
            {
                field: "invoice",
                headerName: "Supplier Invoice",
                width: 155,
            },
            {
                field: "product",
                headerName: "Drug / Product",
                minWidth: 220,
                flex: 1,
            },
            {
                field: "totqty",
                headerName: "Qty",
                width: 85,
                align: "right",
                headerAlign: "right",
            },
            {
                field: "batch",
                headerName: "Batch No",
                width: 125,
            },
            {
                field: "expiry",
                headerName: "Expiry",
                width: 110,
            },
            {
                field: "cgst_percent",
                headerName: "CGST %",
                width: 95,
                align: "right",
                headerAlign: "right",
                valueFormatter: (value) =>
                    `${getNumber(value).toFixed(2)}%`,
            },
            {
                field: "sgst_percent",
                headerName: "SGST %",
                width: 95,
                align: "right",
                headerAlign: "right",
                valueFormatter: (value) =>
                    `${getNumber(value).toFixed(2)}%`,
            },
            {
                field: "cgst_amount",
                headerName: "CGST Amount",
                width: 125,
                align: "right",
                headerAlign: "right",
                valueFormatter: (value) => formatAmount(value),
            },
            {
                field: "sgst_amount",
                headerName: "SGST Amount",
                width: 125,
                align: "right",
                headerAlign: "right",
                valueFormatter: (value) => formatAmount(value),
            },
            {
                field: "gstamount",
                headerName: "Total GST",
                width: 115,
                align: "right",
                headerAlign: "right",
                valueFormatter: (value) => formatAmount(value),
            },
            {
                field: "netamount",
                headerName: "Drug Net Amount",
                width: 145,
                align: "right",
                headerAlign: "right",
                valueFormatter: (value) => formatAmount(value),
            },
            {
                field: "grnamount",
                headerName: "GRN Amount",
                width: 125,
                align: "right",
                headerAlign: "right",
                valueFormatter: (value) =>
                    getNumber(value) ? formatAmount(value) : "",
            },
            {
                field: "invamount",
                headerName: "Invoice Amount",
                width: 135,
                align: "right",
                headerAlign: "right",
                valueFormatter: (value) =>
                    getNumber(value) ? formatAmount(value) : "",
            },
            {
                field: "addedby",
                headerName: "Added By",
                width: 120,
            },
        ],
        []
    );

    // ---------------------------------------------------------
    // USER DROPDOWN
    // ---------------------------------------------------------

    const getUserValue = (user) => {
        return (
            user.username ||
            user.user_login ||
            user.name ||
            user.full_name ||
            user.email ||
            ""
        );
    };

    // ---------------------------------------------------------
    // UI
    // ---------------------------------------------------------

    return (
        <Box sx={{ p: 2, width: "100%" }}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                spacing={1}
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, color: "#1e3a8a" }}
                    >
                        Purchase GST Report
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        Drug-wise GRN purchase and GST breakdown
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    onClick={getPurchaseReport}
                    disabled={isLoading}
                    sx={{
                        backgroundColor: "#2563eb",
                        textTransform: "none",
                        fontWeight: 600,
                    }}
                >
                    {isLoading ? "Loading..." : "Refresh Report"}
                </Button>
            </Stack>

            {/* FILTERS */}

            <Card sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={2}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                From Date
                            </Typography>

                            <DatePicker
                                selected={startDate}
                                onChange={(date) => setStartDate(date)}
                                dateFormat="dd-MM-yyyy"
                                maxDate={endDate || new Date()}
                                className="form-control"
                                placeholderText="Start Date"
                                style={{ width: "100%" }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                To Date
                            </Typography>

                            <DatePicker
                                selected={endDate}
                                onChange={(date) => setEndDate(date)}
                                dateFormat="dd-MM-yyyy"
                                minDate={startDate}
                                maxDate={new Date()}
                                className="form-control"
                                placeholderText="End Date"
                                style={{ width: "100%" }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Added By</InputLabel>

                                <Select
                                    value={addedby}
                                    label="Added By"
                                    onChange={(event) =>
                                        setAddedby(event.target.value)
                                    }
                                >
                                    <MenuItem value="">
                                        All Users
                                    </MenuItem>

                                    {userData.map((user, index) => {
                                        const userValue = getUserValue(user);

                                        return (
                                            <MenuItem
                                                key={user.id ?? index}
                                                value={userValue}
                                            >
                                                {userValue}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Payment Mode</InputLabel>

                                <Select
                                    value={mode}
                                    label="Payment Mode"
                                    onChange={(event) =>
                                        setMode(event.target.value)
                                    }
                                >
                                    <MenuItem value="">
                                        All Modes
                                    </MenuItem>

                                    <MenuItem value="CashPurchase">
                                        Cash Purchase
                                    </MenuItem>

                                    <MenuItem value="CreditPurchase">
                                        Credit Purchase
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={getPurchaseReport}
                                disabled={isLoading}
                                sx={{
                                    height: 40,
                                    textTransform: "none",
                                }}
                            >
                                Apply Filter
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* SUMMARY CARDS */}

            <Grid container spacing={2} sx={{ mb: 2 }}>
                {[
                    {
                        label: "Total GRNs",
                        value: uniqueGrnCount,
                    },
                    {
                        label: "Drug-wise Rows",
                        value: rows.length,
                    },
                    {
                        label: "Total CGST",
                        value: `₹ ${formatAmount(totals.cgst)}`,
                    },
                    {
                        label: "Total SGST",
                        value: `₹ ${formatAmount(totals.sgst)}`,
                    },
                    {
                        label: "Total GST",
                        value: `₹ ${formatAmount(totals.gst)}`,
                    },
                    {
                        label: "Invoice Amount",
                        value: `₹ ${formatAmount(totals.invamount)}`,
                    },
                ].map((item) => (
                    <Grid item xs={6} sm={4} md={2} key={item.label}>
                        <Card
                            sx={{
                                height: "100%",
                                borderRadius: 2,
                                border: "1px solid #e5e7eb",
                            }}
                        >
                            <CardContent sx={{ p: 1.5 }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {item.label}
                                </Typography>

                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                        color: "#1e40af",
                                        mt: 0.5,
                                    }}
                                >
                                    {item.value}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* DRUG-WISE GRID */}

            <Card sx={{ borderRadius: 2 }}>
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700 }}
                    >
                        Drug-wise Purchase Details
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        {rows.length} drug rows from {uniqueGrnCount} GRNs
                    </Typography>
                </Box>

                <Box sx={{ width: "100%" }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={isLoading}
                        getRowId={(row) => row.id}
                        autoHeight
                        disableRowSelectionOnClick
                        pageSizeOptions={[10, 25, 50, 100]}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 25,
                                    page: 0,
                                },
                            },
                        }}
                        slots={{
                            toolbar: GridToolbar,
                        }}
                        sx={{
                            border: 0,

                            "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: "#eff6ff",
                                fontWeight: 700,
                            },

                            "& .MuiDataGrid-columnHeaderTitle": {
                                fontWeight: 700,
                            },

                            "& .MuiDataGrid-cell": {
                                fontSize: "0.82rem",
                            },

                            "& .MuiDataGrid-row:hover": {
                                backgroundColor: "#f8fafc",
                            },

                            "& .MuiDataGrid-footerContainer": {
                                borderTop: "1px solid #e5e7eb",
                            },
                        }}
                    />
                </Box>
            </Card>

            {/* REPORT TOTALS */}

            <Card sx={{ mt: 2, borderRadius: 2 }}>
                <CardContent>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, mb: 2 }}
                    >
                        Purchase GST Summary
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography color="text.secondary" variant="body2">
                                Purchase Before GST
                            </Typography>

                            <Typography variant="h6" fontWeight={700}>
                                ₹ {formatAmount(totalPurchaseBeforeGst)}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Typography color="text.secondary" variant="body2">
                                CGST Amount
                            </Typography>

                            <Typography variant="h6" fontWeight={700}>
                                ₹ {formatAmount(totals.cgst)}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Typography color="text.secondary" variant="body2">
                                SGST Amount
                            </Typography>

                            <Typography variant="h6" fontWeight={700}>
                                ₹ {formatAmount(totals.sgst)}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Typography color="text.secondary" variant="body2">
                                Total GST
                            </Typography>

                            <Typography variant="h6" fontWeight={700}>
                                ₹ {formatAmount(totals.gst)}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Typography color="text.secondary" variant="body2">
                                Total GRN Amount
                            </Typography>

                            <Typography variant="h6" fontWeight={700}>
                                ₹ {formatAmount(totals.grnamount)}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Typography color="text.secondary" variant="body2">
                                Total Invoice Amount
                            </Typography>

                            <Typography variant="h6" fontWeight={700}>
                                ₹ {formatAmount(totals.invamount)}
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {isLoading && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        py: 2,
                    }}
                >
                    <CircularProgress size={28} />
                </Box>
            )}
        </Box>
    );
};

export default PurchaseGstReport;
